import { SelectPicker } from "rsuite";
import { Button } from "reactstrap";
import { useMemo } from "react";

import useTenantUsers from "../../../../api/api-hooks/tenantUsers/useTenantUsers";
import AutoCompleteInput from "../../../../components/Inputs/AutoCompleteInput";
import usePeople from "../../../../api/api-hooks/cloud/usePeople";
import AppSpinner from "../../../../elements/AppSpinner";

interface Props {
  email: string;
  permissionID: string;
  submitting?: boolean;
  permissions: any[];
  permissionsScope?: string;
  useOwner?: boolean;
  useExistingUsers?: boolean;
  idKey?: string;
  css?: string;
  useGlobalUsers?: boolean;
  setEmail: React.Dispatch<React.SetStateAction<string>>;
  members?: any[];
  setPermissionID: (permissionID: string | null) => void;
  userFilter?: (user: any) => boolean;
  onAdd: () => void;
}

const InviteUsers = ({
  email = "", // Email state
  permissionID, // Permission ID state
  submitting = false, // Whether the form is submitting if ASYNC
  permissions, // The permissions to use for the new users.
  permissionsScope = "Cloud", // Scope of the permissions.
  useOwner = false, // Whether or not the owner permission should be shown.
  useExistingUsers = false, // Whether or not to use existing users.
  idKey = "id", // The key to use for the value in the select picker. (For permissions)
  useGlobalUsers = false, // Whether or not to use all users or just current tenant users
  members = [], // The members of the entity (Cloud, Workspace).
  setEmail, // Email state setter
  setPermissionID, // Permission ID state setter
  userFilter, // Used for filtering users in the select picker
  onAdd, // Callback function for when a user is added.
}: Props) => {
  const { users: tUsers } = useTenantUsers(!useExistingUsers || useGlobalUsers);
  const { people: gUsers } = usePeople(!useExistingUsers || !useGlobalUsers);

  const availableUsers = useMemo(() => {
    const users = useExistingUsers ? (useGlobalUsers ? gUsers : tUsers) : [];
    let availableUsers = users ?? [];

    if (userFilter) {
      availableUsers = availableUsers.filter((user: any) => userFilter(user));
    }

    // Removing all the disabled users.
    if (useGlobalUsers) {
      availableUsers = availableUsers.filter((user: any) => !user.Disabled);
    }

    if (!availableUsers) return [];

    return availableUsers.map((user: any) => ({
      label: [user?.Name ?? "", user?.PrimaryContactEmail ?? ""],
      value: user?.PrimaryContactEmail ?? "",
    }));
  }, [tUsers?.length, gUsers?.length, members?.length]);

  return (
    <div className="w-100">
      <div className="d-flex align-items-center w-100">
        <AutoCompleteInput
          availableUsers={availableUsers}
          email={email}
          setEmail={setEmail}
        />
        <SelectPicker
          className="mr-3"
          searchable={false}
          style={{ width: "100%" }}
          placeholder="Assign Permissions"
          data={permissions.filter(
            (p) => (useOwner ? true : !p.Owner) && p.Type === permissionsScope
          )}
          value={permissionID}
          onChange={(newID) => setPermissionID(newID)}
          labelKey={"Name"}
          valueKey={idKey}
          renderMenuItem={(label, item) => {
            return (
              <div style={{ maxWidth: 400 }}>
                <div
                  style={{ fontSize: 14 }}
                  className="text-dark font-weight-500"
                >
                  {label}
                </div>
                <div style={{ fontSize: 10 }}>{item?.Description}</div>
              </div>
            );
          }}
          preventOverflow
        />
        <Button
          color="primary"
          size="sm"
          style={{ height: 36, width: 120 }}
          onClick={() => {
            setEmail("");
            onAdd();
          }}
          disabled={submitting || !email || !permissionID}
        >
          {submitting ? <AppSpinner size="sm" color="white" /> : "Add"}
        </Button>
      </div>
    </div>
  );
};

export default InviteUsers;
